import { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ApiManager from "../../api/ApiManager";
import { useAuth } from "../../store/AuthContext/AuthContext";
import { toast } from "react-hot-toast";

export const useResearchAPI = ({
    setSearchResponses,
    setCurrentPrompt,
    setSelectedChat,
    setSelectedDocumentsChat
}) => {
    // API and loading states
    const [loading, setLoading] = useState(false);
    const [chatId, setChatId] = useState(null);
    const [urlSearchQuery, setUrlSearchQuery] = useState(null);
    const [searchData, setSearchData] = useState(null);

    // Initialize only once
    const initialSearchCalled = useRef(false);

    // Auth / API
    const { getCookie } = useAuth();
    const userSub = getCookie("auth_token");
    const projectId = getCookie("project_id");
    const id_token = getCookie("id_token");
    const api = new ApiManager();

    // React-router hooks
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Only run on mount
    useEffect(() => {
        // Read the URL parameters once
        const chatParam = searchParams.get("chat");
        const queryParam = searchParams.get("query");

        if (chatParam) {
            setChatId(chatParam);
        }
        if (queryParam) {
            setUrlSearchQuery(queryParam);
        }

        // Remove the `query` param, but keep chatParam if it exists
        const newParams = new URLSearchParams();
        if (chatParam) newParams.set("chat", chatParam);

        navigate(
            {
                pathname: "/client/research",
                search: newParams.toString() ? `?${newParams.toString()}` : "",
            },
            { replace: true }
        );
    }, []);

    // For incoming query (only once, when urlSearchQuery exists)
    useEffect(() => {
        if (
            urlSearchQuery &&
            !isUUID(urlSearchQuery) &&
            !initialSearchCalled.current
        ) {
            initialSearchCalled.current = true;
            callSearchAPI(urlSearchQuery);
        } else if (!urlSearchQuery) {
            getChats();
        }
    }, [urlSearchQuery]);

    // If a historic chat is loaded via URL query (optional)
    useEffect(() => {
        if (urlSearchQuery && searchData?.history) {
            const fullId = "history#" + urlSearchQuery;
            const chatItem = searchData.history.find((item) => item.sk === fullId);
            if (chatItem) {
                const parsedChat =
                    typeof chatItem.chat === "string"
                        ? JSON.parse(chatItem.chat)
                        : chatItem.chat;
                const combinedDocuments = [
                    ...(parsedChat?.documents || []),
                    ...(parsedChat?.images || []),
                    ...(parsedChat?.urls || []),
                    ...(parsedChat?.semanticDocument || []),
                ];
                setSelectedChat(parsedChat.ai_response);
                setSelectedDocumentsChat(combinedDocuments);
                initialSearchCalled.current = true;
            }
        }
    }, [urlSearchQuery, searchData]);

    // API call to send a new prompt
    const callSearchAPI = (prompt) => {
        if (!prompt.trim() || loading) return;

        setCurrentPrompt(prompt);
        setLoading(true);
        // Add a placeholder for the answer skeleton
        setSearchResponses((prev) => [...prev, { aiContent: null }]);

        const payload = {
            project_id: projectId,
            user_sub: userSub,
            prompt,
            // Include chatId if it already exists (follow-up chat)
            ...(chatId && { chat_id: chatId }),
        };

        console.log(payload)

        api
            .post("/search", payload, { headers: { Authorization: `Bearer ${id_token}` } })
            .then((response) => {
                setLoading(false);

                // Handle error response from the API
                if (
                    response.data.ai_response.content ===
                    "Non sono riuscito a generare una risposta. Riprova più tardi."
                ) {
                    toast.error("Non sono riuscito a generare una risposta. Riprova più tardi.");
                    setSearchResponses((prev) => {
                        if (prev.length && prev[prev.length - 1].aiContent === null) {
                            return prev.slice(0, -1);
                        }
                        return prev;
                    });
                    return;
                }

                const newChatId = response.data.chat_id;
                const newResponse = {
                    aiContent: response.data.ai_response,
                    userDocument: response.data.documents?.items || [],
                    userImages: response.data.images?.items || [],
                    userUrls: response.data.urls?.items || [],
                    semanticDocument: response.data.semantic_scholar_results || [],
                };

                console.log("New response:", newResponse);

                // For a brand-new chat, newChatId is returned and we update the URL
                if (!chatId && newChatId) {
                    setChatId(newChatId);
                    navigate(`/client/research?chat=${newChatId}`, { replace: true });
                }
                // For a follow-up, just update the conversation responses
                setSearchResponses((prev) => {
                    if (prev.length && prev[prev.length - 1].aiContent === null) {
                        const updated = [...prev];
                        updated[updated.length - 1] = newResponse;
                        return updated;
                    }
                    return [...prev, newResponse];
                });
            })
            .catch((error) => {
                setLoading(false);
                toast.error("Stiamo avendo dei problemi, riprova più tardi");
                setSearchResponses((prev) => {
                    if (prev.length && prev[prev.length - 1].aiContent === null) {
                        return prev.slice(0, -1);
                    }
                    return prev;
                });
                console.error(error);
            });
    };

    const getSingleChatContent = async (id) => {
        if (!id) {
            console.error("getSingleChatContent: 'id' is required.");
            return;
        }

        try {
            const res = await api.get(`/search/get?project_id=${projectId}&chat_id=${id}`, {headers: { Authorization: `Bearer ${id_token}` }});
            const sortedValidHistory = res.data.history
                .map(h => ({
                    ...h,
                    parsedChat: JSON.parse(h.chat),
                }))
                .filter(h => {
                    const content = h.parsedChat?.ai_response?.content;
                    return content && content.trim() !== "Non sono riuscito a generare una risposta. Riprova più tardi.";
                })
                .sort((a, b) => new Date(a.CreationDate) - new Date(b.CreationDate));

            return {
                fullHistory: sortedValidHistory,
            };
        } catch (error) {
            console.error(error);
            toast.error("Errore nel caricamento della chat: dati non disponibili.");
            return null;
        }
    };


    // Fetch all chats for the project
    const getChats = () => {
        api.get(`/search/get?project_id=${projectId}`, { headers: { Authorization: `Bearer ${id_token}` } })
            .then((res) => {
                try {
                    setSearchData({ history: res.data.history });
                } catch (error) {
                    console.error("Error parsing response:", error);
                    setSearchData({ history: [] });
                }
            })
            .catch((error) => {
                console.error("Error retrieving chats:", error);
                setSearchData({ history: [] });
            });
    };

    // Simple UUID checker
    const isUUID = (str) => {
        const uuidPattern =
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        return uuidPattern.test(str);
    };

    return {
        loading,
        chatId,
        urlSearchQuery,
        searchData,
        callSearchAPI,
        getSingleChatContent
    };
};